import { useMemo } from 'react';

import Tippy from '@tippyjs/react';

import { useTableContext } from 'lib/context/TableContext/TableContext';
import { useUIContext } from 'lib/context/UIContext/UIContext';
import { camelCaseToReadableFormat, ia, io, pluralize, singularize } from 'lib/helpers/utility';

import DisplayButton from 'components/shared/AGTable/DisplayButton';
import Button from 'components/shared/Buttons/Button';
import Export from 'components/shared/Export/Export';
import Filter from 'components/shared/Filter/Filter';
import Header from 'components/shared/Header/Header';
import Popover from 'components/shared/Popovers/Popover/Popover';
import Typography from 'components/shared/Typography/Typography';

import { SPECIAL_UPPERCASE_NAMES } from '../lib/constants';
import { ALLOWED_PRINT_EXPORT_CATEGORIES } from '../lib/helpers';

import ViewTabs from './ViewTabs';

const TableHeader = ({
  category,
  onModalToggle,
  hasNew,
  subtitle,
  actions,
  refresh,
  onRefresh,
  noTitle,
  switchItems,
  rightActions,
  loading,
  newButtonText,
  statusPanelData = [],
  keepOldFilters
}) => {
  const tableContext = useTableContext();

  const {
    filters,
    setFilters,
    defaultFilters,
    pagination,
    setPage,
    title,
    setInitialFilters,
    isEditingFilter,
    setIsEditingFilter,
    gridApi,
    ...rest
  } = tableContext;

  const { device } = useUIContext();

  const isMobile = device === 'mobile' || device === 'tablet-sm';
  const isTablet = device === 'tablet' || device === 'tablet-lg';
  const isSmallerDevice = isMobile || isTablet;

  const totalSelected = useMemo(() => {
    return rest.selectedRows?.length;
  }, [rest.selectedRows]);

  const categoryName = SPECIAL_UPPERCASE_NAMES.includes(category)
    ? category.toUpperCase()
    : camelCaseToReadableFormat(category);

  const printExportConfig = useMemo(() => ALLOWED_PRINT_EXPORT_CATEGORIES[category], [category]);

  const onFilterChange = (callback) => {
    setPage(1);
    setFilters(callback);
  };

  const smallDeviceOptions = [
    isMobile &&
      hasNew && {
        label: `New ${singularize(categoryName)}`,
        onClick: onModalToggle,
        icon: 'add-square',
        color: 'primary'
      }
  ].filter(Boolean);

  const options = smallDeviceOptions.filter(Boolean);

  return (
    <Header
      title={!noTitle && (title || categoryName)}
      subtitle={subtitle}
      childrenClassName="flex items-center gap-3 justify-between flex-1">
      {!keepOldFilters && (
        <ViewTabs
          items={switchItems}
          category={category}
          filters={filters}
          title={title}
          setFilters={setFilters}
          defaultFilters={defaultFilters}
          setInitialFilters={setInitialFilters}
          isEditingFilter={isEditingFilter}
          setIsEditingFilter={setIsEditingFilter}
        />
      )}
      <div className="flex flex-1 items-center justify-end gap-3">
        {typeof actions === 'function'
          ? actions({ category, filters, loading, setFilters, ...rest, gridApi })
          : actions}
        {totalSelected > 0 && actions && (
          <Typography color="neutral-600" size="small" className="whitespace-nowrap">
            {totalSelected} {pluralize('item', totalSelected)} selected
          </Typography>
        )}
        {!isSmallerDevice && refresh && (
          <Tippy content="Refresh" placement="bottom" theme="dark">
            <Button
              icon="refresh-linear"
              outlined
              color="neutral"
              iconIsStroke
              onClick={onRefresh}
            />
          </Tippy>
        )}
        {pagination && keepOldFilters && <DisplayButton />}
        {io(filters) && keepOldFilters && (
          <Filter
            category={category}
            defaultFilters={defaultFilters}
            filters={filters}
            setFilters={onFilterChange}
            menuPortalTarget={document.body}
          />
        )}

        <Export
          showLabel
          useData={() => ({
            ...tableContext,
            statusPanelData:
              printExportConfig?.withTotals &&
              statusPanelData.map(({ field, label }) => ({ field, label }))
          })}
        />

        {!isMobile && hasNew && (
          <Button
            text={`New ${singularize(newButtonText || categoryName)}`}
            icon="add-square-mono"
            size="small"
            transparent
            iconColor="white"
            onClick={onModalToggle}
            textClassName="whitespace-nowrap"
          />
        )}
      </div>
      {typeof rightActions === 'function'
        ? rightActions({ category, loading, filters, setFilters, ...rest })
        : rightActions}
      {ia(options) && (
        <Popover options={options} position="left" optionIconColor="neutral" isBorder={false} />
      )}
    </Header>
  );
};

export default TableHeader;
